import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { CourseParticipant, DozentService } from '../../../../services/dozent.service';
import { DozentSocketService } from '../../../../services/dozent-socket.service';
import { DispatchExerciseDialogComponent } from '../../../../components/dialogs/dispatch-exercise-dialog/dispatch-exercise-dialog.component';
import { participantPseudonym } from '../../../../helpers/participant-pseudonym';
import { CompetencyProfile } from '../../../../components/competency-profile/competency-profile.model';

interface ProfileEntry {
  key: string;
  value: any;
}

interface StructuredFeedback {
  version?: number;
  score?: number;
  max_score?: number;
  questions?: StructuredQuestionFeedback[];
}

interface StructuredQuestionFeedback {
  question_index: number;
  question_id?: string | null;
  question_type?: string | null;
  question_text?: string | null;
  correct?: boolean;
  points?: number;
  max_points?: number;
  feedback?: string | null;
  explanation?: string | null;
  selected?: string[];
  correct_answers?: string[];
  option_results?: StructuredOptionResult[];
  blank_results?: StructuredBlankResult[];
}

interface StructuredOptionResult {
  text: string;
  selected?: boolean;
  correct?: boolean;
  status?: string;
}

interface StructuredBlankResult {
  index?: number;
  submitted?: string;
  expected?: string;
  correct?: boolean;
}

@Component({
  selector: 'app-participant-detail',
  templateUrl: './participant-detail.component.html',
  styleUrl: './participant-detail.component.scss',
})
export class ParticipantDetailComponent implements OnInit, OnDestroy {
  @Input() participant!: CourseParticipant;
  @Input() courses: any[] = [];
  @Input() currentCourseId: string | null = null;
  @Output() back = new EventEmitter<void>();

  activeTab = 'general';

  // General info
  enrollments: any[] = [];
  isLoadingEnrollments = false;
  enrollmentPage = 0;
  enrollmentPageSize = 25;
  readonly pageSizeOptions = [25, 50, 100];

  // Learner profile
  learnerProfile: any = null;
  isLoadingProfile = false;
  /** Kompetenzprofil (PRD competency-profile) — loaded when its tab opens. */
  competencyProfile: CompetencyProfile | null = null;
  isLoadingCompetency = false;
  competencyError = false;
  private competencyLoaded = false;
  expandedObservation: number | null = null;

  // Exercises
  exercises: any[] = [];
  isLoadingExercises = false;
  expandedExercise: string | null = null;
  exercisePage = 0;
  exercisePageSize = 25;

  // Conversations
  conversations: any[] = [];
  isLoadingConversations = false;
  conversationPage = 0;
  conversationPageSize = 25;
  showNewMessageForm = false;
  newConvMessage = '';
  isCreatingConversation = false;

  // Online status
  isOnline = false;
  lastSeen: string | null = null;

  private subs: Subscription[] = [];

  constructor(
    private dozentService: DozentService,
    private dialogService: NbDialogService,
    private toastr: NbToastrService,
    private t: TranslateService,
    private socketService: DozentSocketService,
  ) {}

  ngOnInit(): void {
    this.loadGeneral();
    this.loadOnlineStatus();
    this.wireLiveUpdates();
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.subs = [];
  }

  /**
   * Hot-load wireup. The participant-detail page is the dozent's
   * deepest learner view — exercises, conversations, presence. We
   * listen to the dozent socket and patch the local arrays so the
   * dozent sees changes without tab-switching or reloading.
   *
   * Filters are scoped to THIS learner — if an event is for a
   * different participant we ignore it.
   */
  private wireLiveUpdates(): void {
    // Generic notification stream. We route per `type`:
    //   • `ai_escalation` / `new_message` → re-load conversations
    //     so the new card / preview appears without a refresh.
    //   • `submission_review` → re-load exercises if that tab is
    //     open, so a just-submitted answer + AI grading shows up.
    // Single channel keeps frontend wiring simple — backend producers
    // pick a `type` and call `create_and_push_*_notification`.
    this.subs.push(
      this.socketService.notification$.subscribe((notif) => {
        if (!notif) return;
        switch (notif.type) {
          case 'ai_escalation':
          case 'new_message':
            if (this.activeTab === 'communication') {
              this.loadConversations();
            }
            break;
          case 'submission_review':
            if (this.activeTab === 'exercises') {
              this.loadExercises();
            }
            break;
        }
      })
    );

    // Same-thread message append (already-selected conversation).
    this.subs.push(
      this.socketService.newMessage$.subscribe((msg) => {
        if (!msg?.conversation_id) return;
        const idx = this.conversations.findIndex(
          (c) => c.id === msg.conversation_id
        );
        if (idx === -1) return;
        const updated = {
          ...this.conversations[idx],
          last_message_preview:
            msg.message ?? this.conversations[idx].last_message_preview,
          last_message_at: new Date().toISOString(),
          unread_count: (this.conversations[idx].unread_count ?? 0) + 1,
        };
        this.conversations = [
          updated,
          ...this.conversations.slice(0, idx),
          ...this.conversations.slice(idx + 1),
        ];
      })
    );
  }

  onTabChange(tab: string) {
    this.activeTab = tab;
    if (tab === 'profile' && !this.learnerProfile) {
      this.loadLearnerProfile();
      // The profile's radar, mastery bars and trend come from the same
      // learner's competency profile (PRD mastery-objectives M1).
      if (!this.competencyLoaded && !this.isLoadingCompetency) this.loadCompetencyProfile();
    } else if (tab === 'learning-state' && !this.competencyLoaded) {
      this.loadCompetencyProfile();
    } else if (tab === 'exercises' && this.exercises.length === 0) {
      this.loadExercises();
    } else if (tab === 'communication' && this.conversations.length === 0) {
      this.loadConversations();
    }
  }

  loadGeneral() {
    this.isLoadingEnrollments = true;
    const email = this.participant.email;
    const userId = this.participant.learner_user_id;

    // Load participants for each course and find this participant's enrollments
    let pending = this.courses.length;
    if (pending === 0) {
      this.isLoadingEnrollments = false;
      return;
    }

    this.enrollments = [];
    for (const course of this.courses) {
      this.dozentService.listParticipants(course.id).subscribe({
        next: (participants: CourseParticipant[]) => {
          const match = participants.find(
            (p) =>
              (email && p.email === email) ||
              (userId && p.learner_user_id === userId)
          );
          if (match) {
            this.enrollments.push({
              course_name: course.name || course.course_name,
              course_id: course.id,
              enrolled_at: match.enrolled_at,
              enrollment_status: match.enrollment_status,
              enrollment_source: match.enrollment_source,
              grade: match.moodle_grade_total || '—',
            });
          }
          pending--;
          if (pending === 0) {
            this.clampEnrollmentPage();
            this.isLoadingEnrollments = false;
          }
        },
        error: () => {
          pending--;
          if (pending === 0) {
            this.clampEnrollmentPage();
            this.isLoadingEnrollments = false;
          }
        },
      });
    }
  }

  loadOnlineStatus() {
    if (!this.participant.learner_user_id) return;
    this.dozentService.getOnlineStatuses([this.participant.learner_user_id]).subscribe({
      next: (data: any) => {
        const status = data[this.participant.learner_user_id];
        if (status) {
          this.isOnline = status.is_online;
          this.lastSeen = status.last_seen;
        }
      },
    });
  }

  loadLearnerProfile() {
    if (!this.participant.learner_user_id) return;
    this.isLoadingProfile = true;
    this.dozentService.getParticipantLearnerProfile(this.participant.learner_user_id).subscribe({
      next: (data: any) => {
        this.learnerProfile = data;
        this.isLoadingProfile = false;
      },
      error: () => (this.isLoadingProfile = false),
    });
  }

  loadCompetencyProfile() {
    if (!this.participant.learner_user_id) return;
    this.isLoadingCompetency = true;
    this.competencyError = false;
    this.dozentService
      .getParticipantCompetencyProfile(this.participant.learner_user_id)
      .pipe(finalize(() => (this.isLoadingCompetency = false)))
      .subscribe({
        next: (data: CompetencyProfile | null) => {
          this.competencyLoaded = true;
          this.competencyProfile = data;
          this.competencyError = !data;
        },
        error: () => (this.competencyError = true),
      });
  }

  loadExercises() {
    if (!this.participant.learner_user_id) return;
    this.isLoadingExercises = true;
    this.dozentService
      .getParticipantExercises(this.participant.learner_user_id, this.currentCourseId)
      .subscribe({
        next: (data: any[]) => {
          this.exercises = data;
          this.clampExercisePage();
          this.isLoadingExercises = false;
        },
        error: () => (this.isLoadingExercises = false),
      });
  }

  loadConversations() {
    if (!this.participant.learner_user_id) return;
    this.isLoadingConversations = true;
    this.dozentService
      .getParticipantConversations(this.participant.learner_user_id, this.currentCourseId)
      .subscribe({
        next: (data: any[]) => {
          this.conversations = data;
          this.clampConversationPage();
          this.isLoadingConversations = false;
        },
        error: () => (this.isLoadingConversations = false),
      });
  }

  exerciseRowId(ex: any): string {
    return ex?.submission_id || ex?.assignment_id || '';
  }

  toggleExercise(ex: any) {
    const id = this.exerciseRowId(ex);
    if (!id) return;
    this.expandedExercise = this.expandedExercise === id ? null : id;
  }

  structuredFeedback(ex: any): StructuredFeedback | null {
    return ex?.dozent_feedback_structured ?? ex?.ai_feedback_structured ?? null;
  }

  structuredQuestions(ex: any): StructuredQuestionFeedback[] {
    const structured = this.structuredFeedback(ex);
    return Array.isArray(structured?.questions) ? structured.questions : [];
  }

  hasStructuredReview(ex: any): boolean {
    return this.structuredQuestions(ex).length > 0;
  }

  resultScore(ex: any): number | null {
    const structuredScore = this.structuredFeedback(ex)?.score;
    return ex?.dozent_score ?? ex?.ai_score ?? structuredScore ?? null;
  }

  resultMaxScore(ex: any): number | null {
    const structuredMax = this.structuredFeedback(ex)?.max_score;
    return structuredMax ?? ex?.max_score ?? null;
  }

  resultFeedback(ex: any): string {
    return ex?.dozent_feedback ?? ex?.ai_feedback ?? '';
  }

  resultSource(ex: any): 'ai' | 'dozent' {
    if (
      ex?.feedback_release_source === 'dozent' ||
      ex?.dozent_score != null ||
      !!ex?.dozent_feedback ||
      !!ex?.dozent_feedback_structured
    ) {
      return 'dozent';
    }
    return 'ai';
  }

  resultSourceLabelKey(ex: any): string {
    return this.resultSource(ex) === 'dozent'
      ? 'pages.dozent.participants.resultBadgeDozent'
      : 'pages.dozent.participants.resultBadgeAi';
  }

  resultSourceClass(ex: any): string {
    return this.resultSource(ex) === 'dozent' ? 'source-dozent' : 'source-ai';
  }

  questionStatusClass(result: StructuredQuestionFeedback): string {
    return result?.correct ? 'is-correct' : 'is-incorrect';
  }

  hasOptionResults(result: StructuredQuestionFeedback): boolean {
    return Array.isArray(result?.option_results) && result.option_results.length > 0;
  }

  hasBlankResults(result: StructuredQuestionFeedback): boolean {
    return Array.isArray(result?.blank_results) && result.blank_results.length > 0;
  }

  optionStatusClass(option: StructuredOptionResult): string {
    switch (option?.status) {
      case 'correct_selected':
        return 'correct-selected';
      case 'incorrect_selected':
        return 'incorrect-selected';
      case 'correct_missing':
        return 'correct-missing';
      default:
        return 'neutral';
    }
  }

  optionStatusLabelKey(option: StructuredOptionResult): string {
    switch (option?.status) {
      case 'correct_selected':
        return 'pages.dozent.participants.answerCorrectSelected';
      case 'incorrect_selected':
        return 'pages.dozent.participants.answerWrongSelected';
      case 'correct_missing':
        return 'pages.dozent.participants.answerCorrectMissing';
      default:
        return 'pages.dozent.participants.answerNeutral';
    }
  }

  optionStatusSymbol(option: StructuredOptionResult): string {
    switch (option?.status) {
      case 'correct_selected':
      case 'correct_missing':
        return '✓';
      case 'incorrect_selected':
        return '✕';
      default:
        return '';
    }
  }

  formatValues(values?: string[] | null): string {
    if (!Array.isArray(values)) return '';
    return values.map((value) => String(value ?? '')).filter(Boolean).join(', ');
  }

  questionList(ex: any): any[] {
    const content = ex?.exercise_content;
    if (!content) return [];
    if (Array.isArray(content?.questions)) return content.questions;
    if (Array.isArray(content)) return content;
    return [];
  }

  getQuestionText(q: any, index: number): string {
    return (
      q?.problemStatement ??
      q?.problem_statement ??
      q?.question ??
      q?.text ??
      q?.title ??
      `Question ${index + 1}`
    );
  }

  getCorrectAnswer(q: any): string {
    const ans = q?.answer ?? q?.correct_answer;
    if (ans === null || ans === undefined || ans === '') return '';
    if (typeof ans === 'string') return ans;
    if (ans && typeof ans === 'object' && Array.isArray(ans.correct_answers)) {
      return ans.correct_answers
        .map((v: any) => String(v ?? ''))
        .filter(Boolean)
        .join(', ');
    }
    if (Array.isArray(ans)) {
      if (ans.length && typeof ans[0] === 'object' && 'is_correct' in ans[0]) {
        return ans
          .filter((o: any) => o?.is_correct)
          .map((o: any) => o?.text ?? '')
          .filter(Boolean)
          .join(', ');
      }
      return ans.map((v: any) => String(v ?? '')).filter(Boolean).join(', ');
    }
    return String(ans);
  }

  answerFor(ex: any, q: any, index: number): string {
    if (!ex?.answers) return '';
    const answers = ex.answers;
    const raw =
      answers[q?.id] ??
      answers[index] ??
      answers[`q${index}`] ??
      answers[String(index)] ??
      '';
    return this.formatAnswer(raw);
  }

  rawAnswerEntries(ex: any): ProfileEntry[] {
    if (!this.isObject(ex?.answers)) return [];
    return Object.entries(ex.answers).map(([key, value]) => ({
      key,
      value: this.formatAnswer(value),
    }));
  }

  formatAnswer(raw: any): string {
    if (raw === null || raw === undefined || raw === '') return '';
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);
    if (Array.isArray(raw)) {
      return raw
        .map((v) => (typeof v === 'string' ? v : v?.text ?? JSON.stringify(v)))
        .filter((v) => v !== '')
        .join(', ');
    }
    if (typeof raw === 'object') {
      if ('text' in raw) return String(raw.text ?? '');
      return Object.values(raw)
        .map((v) => (typeof v === 'string' ? v : JSON.stringify(v)))
        .filter((v) => v !== '')
        .join(', ');
    }
    return String(raw);
  }

  get pagedEnrollments(): any[] {
    const maxPage = Math.max(Math.ceil(this.enrollments.length / this.enrollmentPageSize) - 1, 0);
    const page = Math.min(this.enrollmentPage, maxPage);
    const start = page * this.enrollmentPageSize;
    return this.enrollments.slice(start, start + this.enrollmentPageSize);
  }

  onEnrollmentPageChange(page: number): void {
    const maxPage = Math.max(Math.ceil(this.enrollments.length / this.enrollmentPageSize) - 1, 0);
    this.enrollmentPage = Math.max(0, Math.min(page, maxPage));
  }

  onEnrollmentPageSizeChange(size: number): void {
    this.enrollmentPageSize = Math.min(Math.max(size, 1), 100);
    this.enrollmentPage = 0;
  }

  get pagedExercises(): any[] {
    const maxPage = Math.max(Math.ceil(this.exercises.length / this.exercisePageSize) - 1, 0);
    const page = Math.min(this.exercisePage, maxPage);
    const start = page * this.exercisePageSize;
    return this.exercises.slice(start, start + this.exercisePageSize);
  }

  onExercisePageChange(page: number): void {
    const maxPage = Math.max(Math.ceil(this.exercises.length / this.exercisePageSize) - 1, 0);
    this.exercisePage = Math.max(0, Math.min(page, maxPage));
  }

  onExercisePageSizeChange(size: number): void {
    this.exercisePageSize = Math.min(Math.max(size, 1), 100);
    this.exercisePage = 0;
  }

  get pagedConversations(): any[] {
    const maxPage = Math.max(Math.ceil(this.conversations.length / this.conversationPageSize) - 1, 0);
    const page = Math.min(this.conversationPage, maxPage);
    const start = page * this.conversationPageSize;
    return this.conversations.slice(start, start + this.conversationPageSize);
  }

  onConversationPageChange(page: number): void {
    const maxPage = Math.max(Math.ceil(this.conversations.length / this.conversationPageSize) - 1, 0);
    this.conversationPage = Math.max(0, Math.min(page, maxPage));
  }

  onConversationPageSizeChange(size: number): void {
    this.conversationPageSize = Math.min(Math.max(size, 1), 100);
    this.conversationPage = 0;
  }

  getParticipantName(): string {
    return participantPseudonym(this.participant);
  }

  isObject(value: any): boolean {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  // ── Communication: start conversation ──

  onStartConversation() {
    if (!this.newConvMessage.trim() || !this.participant.learner_user_id) return;
    this.isCreatingConversation = true;
    this.dozentService
      .createConversation({
        learner_user_id: this.participant.learner_user_id,
        course_id: this.currentCourseId ?? undefined,
        initial_message: this.newConvMessage.trim(),
      })
      .subscribe({
        next: (conv: any) => {
          this.conversations.unshift(conv);
          this.conversationPage = 0;
          this.showNewMessageForm = false;
          this.newConvMessage = '';
          this.isCreatingConversation = false;
          this.toastr.success(
            this.t.instant('pages.dozent.participants.profile.conversationCreated'),
            ''
          );
        },
        error: () => {
          this.isCreatingConversation = false;
          this.toastr.danger(this.t.instant('toasts.error'), '');
        },
      });
  }

  // ── Exercises: assign exercise ──

  openAssignExerciseDialog() {
    // Assign flow, not create flow: pick one of the dozent's PUBLISHED
    // exercises and dispatch it to this learner (pre-ticked).
    this.dialogService.open(DispatchExerciseDialogComponent, {
      context: {
        courseId: this.participant.course_id,
        participants: [this.participant],
        publishedOnly: true,
        preselectParticipants: true,
        onDispatched: () => this.loadExercises(),
      },
    });
  }

  private clampEnrollmentPage(): void {
    const maxPage = Math.max(Math.ceil(this.enrollments.length / this.enrollmentPageSize) - 1, 0);
    this.enrollmentPage = Math.max(0, Math.min(this.enrollmentPage, maxPage));
  }

  private clampExercisePage(): void {
    const maxPage = Math.max(Math.ceil(this.exercises.length / this.exercisePageSize) - 1, 0);
    this.exercisePage = Math.max(0, Math.min(this.exercisePage, maxPage));
  }

  private clampConversationPage(): void {
    const maxPage = Math.max(Math.ceil(this.conversations.length / this.conversationPageSize) - 1, 0);
    this.conversationPage = Math.max(0, Math.min(this.conversationPage, maxPage));
  }
}
